import type { RosettaSDK } from "@product/rosetta-sdk";
import { useAsync } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";

import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { useCurrentArticleIds } from "~/lib/current-item/hooks";
import { rosettaAtom } from "~/lib/rosetta";

import type { BillingCycle, Plan, Resource } from "./types";

export const usePricing = () => {
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const [{ result }, { execute }] = useAsync(async (sdk: RosettaSDK) => {
    const facts = await sdk.loadPostiesFacts();
    return facts?.pricing;
  }, asyncRosettaState?.result?.instance?.rulesEngine?.getFact("posties.pricing"));

  useEffect(() => {
    const sdk = asyncRosettaState?.result?.instance;
    if (!sdk) return;
    void execute(sdk);
  }, [asyncRosettaState?.result?.instance, execute]);

  return {
    pricing: result,
  };
};

export const useTracking = () => {
  const { currentArticleId } = useCurrentArticleIds();
  const trackBillingCycleToggle = useCallback(
    (cycle: BillingCycle) => {
      const cycleLabel = cycle === "1y" ? "Yearly" : "Monthly";
      sendGA4Tracking({
        action: "click",
        category: "subs",
        customized_parameters: {
          article_id: currentArticleId,
          call_to_action: `toggle ${cycleLabel.toLowerCase()} plan`,
          paywall_status: "hard",
          trigger_point: "posties paywall",
        },
        subcategory: "entry_point",
      });
    },
    [currentArticleId],
  );

  const trackPlanSelected = useCallback(
    (plan: Plan) => {
      const { billingCycle, resource } = plan;
      const pianoTermNames: Record<`${Resource}|${BillingCycle}`, string> = {
        "postiesEPaperAndDigital|1m": "Monthly: Posties ePaper + Digital",
        "postiesEPaperAndDigital|1y": "Annual: Posties ePaper + Digital",
        "postiesPrintAndDigital|1m": "Monthly: Posties Print + Digital",
        "postiesPrintAndDigital|1y": "Annual: Posties Print + Digital",
      };
      const planLabel = pianoTermNames[`${resource}|${billingCycle}`];
      sendGA4Tracking({
        action: "click",
        category: "subs",
        customized_parameters: {
          article_id: currentArticleId,
          call_to_action: "choose plan",
          paywall_status: "hard",
          plan_type_select: planLabel,
          trigger_point: "posties paywall",
        },
        subcategory: "entry_point",
      });
    },
    [currentArticleId],
  );

  const trackCompareBenefitClick = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        article_id: currentArticleId,
        call_to_action: "compare benefits",
        paywall_status: "hard",
        trigger_point: "posties paywall",
      },
      subcategory: "entry_point",
    });
  }, [currentArticleId]);

  const trackRedeemClick = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        article_id: currentArticleId,
        call_to_action: "redeem",
        paywall_status: "hard",
        trigger_point: "posties paywall",
      },
      subcategory: "entry_point",
    });
  }, [currentArticleId]);

  return {
    trackBillingCycleToggle,
    trackCompareBenefitClick,
    trackPlanSelected,
    trackRedeemClick,
  };
};
